import isRoleDetailsValid from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Sidebars/UpsertContactSidebar/FormData/constraint/isRoleDetailsValid';
import type BeneficiaryContactView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

type BeneficiaryContactFormArgs = {
	firstName: string;
	lastName: string;
	role: string | null;
	isPersonOfTrust: boolean;
	roleDetails: string | null;
	relationship: string | null;
	phoneNumber: string | null;
	email: string | null;
	notes: string | null;
};

/**
 * `role` and `relationship` carry the RAW enum value, never the `{ value, label }` the store exposes:
 * `fromView` performs the conversion, and `isRoleDetailsValid` relies on comparing `role` to the plain
 * string `'other'` — comparing a `LabeledValueView` here would always be false, silently disabling the
 * conditional requirement.
 *
 * `ignoreNullValue` is deliberately absent: with it, a `null` value would be treated as valid and
 * `isRoleDetailsValid` would never even run. `relationship` starts `null` and must fail validation
 * until the user actually picks a value — exactly like a required field with no pre-selection.
 */
@ValidatedClass()
class BeneficiaryContactFormData extends AbstractValidatedClass<BeneficiaryContactFormArgs> {
	@constraint(Joi.string().max(120), () => 'Le prénom ne doit pas dépasser 120 caractères.')
	@constraint(Joi.string().required(), () => 'Le prénom est obligatoire.')
	public readonly firstName: string;

	@constraint(Joi.string().max(120), () => 'Le nom ne doit pas dépasser 120 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom est obligatoire.')
	public readonly lastName: string;

	@constraint(Joi.string().required(), () => 'Le rôle est obligatoire.')
	public readonly role: string | null;

	public readonly isPersonOfTrust: boolean;

	@constraint(isRoleDetailsValid, 'La précision est requise pour le rôle « Autre ».')
	public readonly roleDetails: string | null;

	@constraint(Joi.string().required(), () => 'Le lien de parenté est obligatoire.')
	public readonly relationship: string | null;

	@constraint(
		Joi.string().max(30).allow(null, ''),
		() => 'Le numéro de téléphone ne doit pas dépasser 30 caractères.',
	)
	public readonly phoneNumber: string | null;

	@constraint(
		Joi.string().email({ tlds: false }).max(180).allow(null, ''),
		() => 'L’adresse e-mail est invalide.',
	)
	public readonly email: string | null;

	@constraint(
		Joi.string().max(1000).allow(null, ''),
		() => 'Les notes ne doivent pas dépasser 1000 caractères.',
	)
	public readonly notes: string | null;

	constructor(data: BeneficiaryContactFormArgs) {
		super(data);
		this.firstName = data.firstName;
		this.lastName = data.lastName;
		this.role = data.role;
		this.isPersonOfTrust = data.isPersonOfTrust;
		this.roleDetails = data.roleDetails;
		this.relationship = data.relationship;
		this.phoneNumber = data.phoneNumber;
		this.email = data.email;
		this.notes = data.notes;
		Object.freeze(this);
	}

	static createEmpty(): BeneficiaryContactFormData {
		return new BeneficiaryContactFormData({
			firstName: '',
			lastName: '',
			role: null,
			isPersonOfTrust: false,
			roleDetails: null,
			relationship: null,
			phoneNumber: null,
			email: null,
			notes: null,
		});
	}

	static fromView(contact: BeneficiaryContactView): BeneficiaryContactFormData {
		return new BeneficiaryContactFormData({
			firstName: contact.firstName,
			lastName: contact.lastName,
			role: contact.role?.value ?? null,
			isPersonOfTrust: contact.isPersonOfTrust,
			roleDetails: contact.roleDetails,
			relationship: contact.relationship.value,
			phoneNumber: contact.phoneNumber,
			email: contact.email,
			notes: contact.notes,
		});
	}

	setFirstName(firstName: string): this {
		return this.cloneWith({ firstName } as Partial<BeneficiaryContactFormArgs>);
	}

	setLastName(lastName: string): this {
		return this.cloneWith({ lastName } as Partial<BeneficiaryContactFormArgs>);
	}

	/**
	 * Leaving "other" clears the stale precision in the same clone, mirroring the server, which nulls
	 * `roleDetails` the instant `role` stops being `other`: without this, a phantom precision would
	 * stay visible on screen while the server silently drops it on save.
	 */
	setRole(role: string | null): this {
		return this.cloneWith({
			role,
			roleDetails: role === 'other' ? this.roleDetails : null,
		} as Partial<BeneficiaryContactFormArgs>);
	}

	setIsPersonOfTrust(isPersonOfTrust: boolean): this {
		return this.cloneWith({ isPersonOfTrust } as Partial<BeneficiaryContactFormArgs>);
	}

	setRoleDetails(roleDetails: string): this {
		return this.cloneWith({ roleDetails } as Partial<BeneficiaryContactFormArgs>);
	}

	setRelationship(relationship: string | null): this {
		return this.cloneWith({ relationship } as Partial<BeneficiaryContactFormArgs>);
	}

	setPhoneNumber(phoneNumber: string): this {
		return this.cloneWith({ phoneNumber } as Partial<BeneficiaryContactFormArgs>);
	}

	setEmail(email: string): this {
		return this.cloneWith({ email } as Partial<BeneficiaryContactFormArgs>);
	}

	setNotes(notes: string): this {
		return this.cloneWith({ notes } as Partial<BeneficiaryContactFormArgs>);
	}
}

export default BeneficiaryContactFormData;
